import { getAvailableResult, getNullAvailableResult, type DependencyResult } from "DependencyResult";
import type { DependencyArgument, DependencyVisitorContext } from "DependencyVisitorContext";
import { mapData, mapDataOne, operationTypes, type MappedReturnType } from "DependencyVisitorUtils";
import { DependencyError } from "Errors";
import { capitalizeWords, ensureIsString } from "StringUtils";

const lengthCore = getMapperWithZeroArgs("Length", (v) => v.length);
const toLowerCore = getMapperWithZeroArgs("ToLower", (v) => v.toLowerCase());
const toUpperCore = getMapperWithZeroArgs("ToUpper", (v) => v.toUpperCase());
const urlEncodeCore = getMapperWithZeroArgs("UrlEncode", encodeURIComponent);

function getMapperWithZeroArgs<T>(
  description: string,
  valueMapper: (value: string) => T,
): (value: unknown) => DependencyResult<T | null> {
  return (value) => {
    if (value == null) {
      return getNullAvailableResult<T>();
    }

    ensureStringValue(description, value);
    return getAvailableResult(valueMapper(value));
  };
}

function ensureStringValue(description: string, value: unknown): asserts value is string {
  if (typeof value !== "string") {
    description = capitalizeWords(description);
    throw new DependencyError(`${description}() can only be used on a string.`);
  }
}

const stringMethodDecorationInfo = Object.freeze({
  operationType: operationTypes.Method,
  operandCount: 1,
});

function length<Mode>(
  string: DependencyArgument,
  data: unknown,
  context: DependencyVisitorContext<Mode>,
): MappedReturnType<Mode, number | null> {
  return mapDataOne(string, data, context, lengthCore, stringMethodDecorationInfo);
}

function toLower<Mode>(
  string: DependencyArgument,
  data: unknown,
  context: DependencyVisitorContext<Mode>,
): MappedReturnType<Mode, string | null> {
  return mapDataOne(string, data, context, toLowerCore, stringMethodDecorationInfo);
}

function toUpper<Mode>(
  string: DependencyArgument,
  data: unknown,
  context: DependencyVisitorContext<Mode>,
): MappedReturnType<Mode, string | null> {
  return mapDataOne(string, data, context, toUpperCore, stringMethodDecorationInfo);
}

function urlEncode<Mode>(
  string: DependencyArgument,
  data: unknown,
  context: DependencyVisitorContext<Mode>,
): MappedReturnType<Mode, string | null> {
  return mapDataOne(string, data, context, urlEncodeCore, stringMethodDecorationInfo);
}

const indexOfCore = getMapperWithOneArg("IndexOf", null, -1, (v, a) => v.indexOf(a));
const endsWithCore = getMapperWithOneArg("EndsWith", false, false, (v, a) => v.endsWith(a));
const startsWithCore = getMapperWithOneArg("StartsWith", false, false, (v, a) => v.startsWith(a));

function getMapperWithOneArg<T>(
  description: string,
  nullStringValue: T,
  nullArgValue: T,
  valueMapper: (value: string, arg: string) => T,
): (values: unknown[]) => DependencyResult<T> {
  return ([string, searchString]) => {
    if (string == null) {
      return getAvailableResult(nullStringValue);
    }

    ensureStringValue(description, string);
    return getAvailableResult(searchString != null ? valueMapper(string, ensureIsString(searchString)) : nullArgValue);
  };
}

const indexOfDecorationInfo = Object.freeze({
  operationType: operationTypes.Method,
  operandCount: 2,
});

function indexOf<Mode>(
  string: DependencyArgument,
  searchString: DependencyArgument,
  data: unknown,
  context: DependencyVisitorContext<Mode>,
): MappedReturnType<Mode, number | null> {
  return mapData([string, searchString], data, context, indexOfCore, indexOfDecorationInfo);
}

const startsEndsWithDecorationInfo = Object.freeze({
  operationType: operationTypes.Method,
  operandCount: 2,
  booleanResult: true,
});

function endsWith<Mode>(
  string: DependencyArgument,
  searchString: DependencyArgument,
  data: unknown,
  context: DependencyVisitorContext<Mode>,
): MappedReturnType<Mode, boolean> {
  return mapData([string, searchString], data, context, endsWithCore, startsEndsWithDecorationInfo);
}

function startsWith<Mode>(
  string: DependencyArgument,
  searchString: DependencyArgument,
  data: unknown,
  context: DependencyVisitorContext<Mode>,
): MappedReturnType<Mode, boolean> {
  return mapData([string, searchString], data, context, startsWithCore, startsEndsWithDecorationInfo);
}

const substringDecorationInfo = Object.freeze({
  operationType: operationTypes.Method,
  operandCount: 3,
});

function substring<Mode>(
  string: DependencyArgument,
  start: DependencyArgument,
  end: DependencyArgument,
  data: unknown,
  context: DependencyVisitorContext<Mode>,
): MappedReturnType<Mode, string | null> {
  return mapData([string, start, end], data, context, substringCore, substringDecorationInfo);
}

function substringCore([string, start, end]: unknown[]): DependencyResult<string | null> {
  if (string == null) {
    return getNullAvailableResult<string>();
  }

  ensureStringValue("Substring", string);
  const startNumber = start != null ? ensureIsNumber(start) : 0;
  const endNumber = end != null ? startNumber + ensureIsNumber(end) : undefined;
  return getAvailableResult(string.substring(startNumber, endNumber));
}

function ensureIsNumber(value: unknown): number {
  if (typeof value === "number") {
    return value;
  }
  throw new Error("Expected a number value.");
}

export default { endsWith, indexOf, length, startsWith, substring, toLower, toUpper, urlEncode };
